import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FilmStrip, Star } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useDragScroll } from "../lib/useDragScroll";
import { timeAgo } from "../lib/timeago";
import SmartImg from "./SmartImg";

const EASE = [0.22, 1, 0.36, 1];
const WEEK = 7 * 86400_000;

/**
 * A quiet row of what was just added — one card per movie / show / celebrity, a batch upload counted as
 * one card. A small label ("New · 2d ago", "+9 new · 1d ago") is the only thing that says "new".
 */
export default function JustDropped({ className = "" }) {
  const [items, setItems] = useState(null);
  const row = useDragScroll();

  useEffect(() => {
    api.get("/scenepacks/just-dropped?limit=12").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section className={className} data-testid="just-dropped">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4060]" aria-hidden="true" /> Just dropped
          </div>
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">New on ALPHA</h2>
        </div>
        <Link to="/scenepacks?sort=latest" className="group hidden sm:inline-flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.18em] text-white/50 hover:text-white transition">
          All latest <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      <div ref={row} className="drag-scroll flex gap-4 overflow-x-auto scrollbar-hide -mx-6 px-6 md:-mx-12 md:px-12 pb-3" data-testid="just-dropped-row">
        {items.map((d, i) => {
          const recent = Date.now() - new Date(d.latest).getTime() < WEEK;
          // "New" / "+9 scenepacks" only while it is fresh; older drops just say how many and when
          const what = recent ? (d.is_new_title ? "New" : `+${d.count} new`) : `${d.count} pack${d.count !== 1 ? "s" : ""}`;
          const label = `${what} · ${timeAgo(d.latest)}`;
          const names = d.characters.join(", ") + (d.characters_count > d.characters.length ? ` +${d.characters_count - d.characters.length}` : "");
          return (
            <motion.div
              key={d.slug}
              className="flex-shrink-0 w-[148px] sm:w-[168px]"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px -8% 0px" }}
              transition={{ duration: 0.7, delay: Math.min(i, 5) * 0.06, ease: EASE }}
            >
              <Link
                to={`/scenepacks/movie/${d.slug}`}
                data-testid={`just-dropped-${d.slug}`}
                className="group block"
                draggable={false}
              >
                <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-[#1A1A1A] border border-white/10 group-hover:border-white/30 shadow-[0_14px_30px_-16px_rgba(0,0,0,0.9)] group-hover:-translate-y-1 transition-[transform,border-color] duration-500">
                  {d.poster ? (
                    <SmartImg src={d.poster} width={480} alt="" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2a1017] to-[#171717]">{d.celebrity ? <Star size={26} className="text-white/20" /> : <FilmStrip size={26} className="text-white/20" />}</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-display font-bold text-[13px] leading-tight line-clamp-2">{d.title}</div>
                  </div>
                </div>
                <div className={`mt-2 text-[10px] font-mono uppercase tracking-[0.14em] truncate ${recent ? "text-[#FF8090]" : "text-white/40"}`}>{label}</div>
                {names && <div className="text-[11px] text-white/40 truncate mt-0.5">{names}</div>}
              </Link>
            </motion.div>
          );
        })}
        <div className="flex-shrink-0 w-2" />
      </div>
    </section>
  );
}
